import { IonIcon } from "@ionic/react";
import Tooltip from "@mui/material/Tooltip";
import { bicycleOutline, calendarOutline, checkmarkCircle, chevronBackOutline, chevronForwardOutline, ellipseOutline, locationOutline, pawOutline, pencilOutline, personOutline, pricetagOutline, swapHorizontalOutline, ticket, ticketOutline } from "ionicons/icons";
import React, { useCallback, useEffect, useMemo } from "react";
import Info from "../../image/icons/map/details/Info.svg?react";
import { getAccessTokenFirstname, getAccessTokenLastname } from "../../services/auth";
import type { TicketPurchaseState } from "../../store/ephemeral";
import { ephemeralStore, ticketPurchaseSlice, useEphemeralSelector } from "../../store/ephemeral";
import { authSlice, usePersistedSlice } from "../../store/persistence";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { formatStopName } from "../../tools/stopNameFormatter";
import type { PurchasableSegmentDTO } from "../../types/dtos/api/purchase/purchase-segments.dto";
import type { SwissCffProviderOffer } from "../../types/dtos/api/sales/provider-offer.dto";
import type { IndexedCffOffer } from "./ticketPurchaseSegments.tools";
import {
    buildCffOfferSelectionModel,
    formatCffOfferLabel,
    isCffJourneyTicket,
    listCffOfferConditions,
    resolveCityAddonSelection,
} from "./ticketPurchaseSegments.tools";
import { TicketSegmentTravelProgress } from "./TicketSegmentTravelProgress";

interface TicketSegmentSelectionProps { "isDarkUi": boolean }

/* Fare condition badges are ready but hidden pending UX validation. */
const SHOW_OFFER_CONDITION_BADGES: boolean = false;

const getOfferTooltipDescription = ( description: string ): React.ReactNode => {
    if ( description.length === 0 ) {
        return null;
    }

    let cleaned = description.replace( /\.\t/gu, " " );

    cleaned = cleaned.replace( /\\n/gu, "\n" );
    cleaned = cleaned.replace( /[ \t]+/gu, " " );
    cleaned = cleaned.trim();

    if ( cleaned.length === 0 ) {
        return null;
    }

    return (
        <span style={{ "whiteSpace": "pre-line" }}>
            {cleaned}
        </span>
    );
};

/** Cosmetic icon per offer — keyword heuristic on the title, no product IDs. */
const getCffOfferIcon = ( offer: IndexedCffOffer ): string => {
    const title = offer.title.toLowerCase();

    if ( title.includes( "journalière" ) || title.includes( "tageskarte" ) || title.includes( "day pass" ) ) {
        return calendarOutline;
    }
    if ( title.includes( "flex" ) ) {
        return swapHorizontalOutline;
    }
    if ( title.includes( "dégriffé" ) || title.includes( "supersaver" ) || title.includes( "spar" ) ) {
        return pricetagOutline;
    }

    return ticketOutline;
};

const formatCityDelta = ( cityPrice: number, journeyPrice?: number ): string => {
    const delta = journeyPrice !== undefined ? cityPrice - journeyPrice : cityPrice;

    return `${ delta >= 0 ? "+" : "−" } ${ Math.abs( delta ).toFixed( 2 ) } CHF`;
};

const formatTime = ( iso: string ): string => {
    try {
        return new Date( iso ).toLocaleTimeString( "fr-CH", {
            "hour":   "2-digit",
            "minute": "2-digit",
        } );
    } catch {
        return "--:--";
    }
};

const formatDateParts = ( iso: string ): {
    "weekday":  string;
    "dayMonth": string;
} => {
    try {
        const date = new Date( iso );

        return {
            "weekday":  date.toLocaleDateString( "fr-CH", { "weekday": "short" } ).replace( ".", "" ),
            "dayMonth": date.toLocaleDateString( "fr-CH", {
                "day":   "2-digit",
                "month": "2-digit",
            } ),
        };
    } catch {
        return {
            "weekday":  "",
            "dayMonth": "",
        };
    }
};

export const TicketSegmentSelection: React.FC<TicketSegmentSelectionProps> = ( { isDarkUi } ) => {
    const palette = getUIColorPalette( isDarkUi );
    const state = useEphemeralSelector( ticketPurchaseSlice, ( s: TicketPurchaseState ) => s );
    const [ auth ] = usePersistedSlice( authSlice );
    const { itinerary, selectedOffers, selectionSegmentIndex, segmentClass, skipSegment } = state;

    const travellerName = useMemo( () => {
        if ( auth.accessToken === null || auth.accessToken === "" ) {
            return "Voyageur";
        }

        const parts = [
            getAccessTokenFirstname( auth.accessToken ),
            getAccessTokenLastname( auth.accessToken ),
        ].filter( ( part ): part is string => part !== null );

        return parts.length > 0 ? parts.join( " " ) : "Voyageur";
    }, [ auth.accessToken ] );

    const purchasableSegments = useMemo( () => {
        if ( itinerary?.offer === undefined ) {
            return [];
        }

        return Object.entries( itinerary.offer.segments ).flatMap<PurchasableSegmentDTO>( ( [ segmentId, segment ] ) => {
            if ( segment.providerOffers === null || segment.providerOffers === undefined ) {
                return [];
            }

            const bundles = segment.providerOffers.filter( ( offer ): offer is SwissCffProviderOffer => offer.provider === "swiss_cff" );

            if ( bundles.length === 0 ) {
                return [];
            }

            const pricingSegment = itinerary.pricingSegments?.find( ( s ) => s.id === segmentId );

            return [
                {
                    "segmentId": segmentId,
                    "from":      {
                        "name": pricingSegment?.from?.name ?? "",
                        "time": pricingSegment?.from?.time ?? "",
                    },
                    "to": {
                        "name": pricingSegment?.to?.name ?? "",
                        "time": pricingSegment?.to?.time ?? "",
                    },
                    "provider": {
                        "id":       "swiss_cff",
                        "metadata": segment.providerProperties?.swiss_cff ?? null,
                    },
                    bundles,
                },
            ];
        } );
    }, [ itinerary ] );

    const currentSegment = purchasableSegments[selectionSegmentIndex];
    const currentClass = currentSegment !== undefined ?  segmentClass[currentSegment.segmentId] ?? "2"  : "2";
    const wantsTicket = currentSegment !== undefined ? !( skipSegment[currentSegment.segmentId] ?? false ) : false;

    const allOffers = useMemo( (): IndexedCffOffer[] => {
        if ( currentSegment === undefined ) {
            return [];
        }

        const offers: IndexedCffOffer[] = [];

        currentSegment.bundles.forEach( ( bundle, bundleIndex ) => {
            bundle.offers.forEach( ( offer, offerIndex ) => {
                offers.push( {
                    ...offer,
                    bundleIndex,
                    offerIndex,
                } );
            } );
        } );

        return offers;
    }, [ currentSegment ] );

    const currentSelection = currentSegment !== undefined ? selectedOffers[currentSegment.segmentId] : undefined;

    const offerModel = useMemo(
        () => buildCffOfferSelectionModel( allOffers, currentClass, currentSelection ),
        [ allOffers, currentClass, currentSelection ],
    );
    const { mainOffers, parcoursOffer, defaultOffer, leadingAddon, tailingAddon, bothAddon, selectedCityKind } = offerModel;

    const isLeadingChecked = selectedCityKind === "leading" || selectedCityKind === "both";
    const isTailingChecked = selectedCityKind === "tailing" || selectedCityKind === "both";

    const selectOffer = useCallback( ( segmentId: string, bundleIndex: number, offerIndex: number, offerIdentifier: string ) => {
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "selectedOffers": {
                ...prev.selectedOffers,
                [segmentId]: {
                    segmentId,
                    bundleIndex,
                    offerIndex,
                    offerIdentifier,
                },
            },
        } ) );
    }, [] );

    const setClass = useCallback( ( value: "1" | "2" ) => {
        if ( currentSegment === undefined ) {
            return;
        }

        const segmentId = currentSegment.segmentId;

        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "segmentClass": {
                ...prev.segmentClass,
                [segmentId]: value,
            },
            "selectedOffers": Object.fromEntries( Object.entries( prev.selectedOffers ).filter( ( [ key ] ) => key !== segmentId ) ),
        } ) );
    }, [ currentSegment ] );

    const toggleWantsTicket = useCallback( ( value: boolean ) => {
        if ( currentSegment === undefined ) {
            return;
        }

        const segmentId = currentSegment.segmentId;

        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "skipSegment": {
                ...prev.skipSegment,
                [segmentId]: !value,
            },
            "selectedOffers": value
                ? prev.selectedOffers
                : Object.fromEntries( Object.entries( prev.selectedOffers ).filter( ( [ key ] ) => key !== segmentId ) ),
        } ) );
    }, [ currentSegment ] );

    const goToNext = useCallback( () => {
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => {
            const nextIndex = prev.selectionSegmentIndex + 1;

            if ( nextIndex >= purchasableSegments.length ) {
                return {
                    ...prev,
                    "step": "summary",
                };
            }

            return {
                ...prev,
                "selectionSegmentIndex": nextIndex,
            };
        } );
    }, [ purchasableSegments.length ] );

    const goToPrevious = useCallback( () => {
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "selectionSegmentIndex": Math.max( 0, prev.selectionSegmentIndex - 1 ),
        } ) );
    }, [] );

    const toggleCityAddon = useCallback( ( kind: "leading" | "tailing" ) => {
        if ( currentSegment === undefined ) {
            return;
        }

        const target = resolveCityAddonSelection( offerModel, kind );

        if ( target === null ) {
            return;
        }

        selectOffer( currentSegment.segmentId, target.bundleIndex, target.offerIndex, target.offerIdentifier ?? "" );
    }, [ currentSegment, offerModel, selectOffer ] );

    useEffect( () => {
        if ( currentSegment === undefined || defaultOffer === null || !wantsTicket ) {
            return;
        }

        const segmentId = currentSegment.segmentId;
        const selection = selectedOffers[segmentId];
        const isValid =
            selection !== undefined &&
            allOffers.some( ( o ) => o.bundleIndex === selection.bundleIndex && o.offerIndex === selection.offerIndex );

        if ( !isValid ) {
            selectOffer( segmentId, defaultOffer.bundleIndex, defaultOffer.offerIndex, defaultOffer.offerIdentifier ?? "" );
        }
    }, [ allOffers, currentClass, currentSegment, defaultOffer, selectedOffers, selectOffer, wantsTicket ] );

    const totalPrice = useMemo( () => {
        let total = 0;

        for ( const selection of Object.values( selectedOffers ) ) {
            const segment = purchasableSegments.find( ( s ) => s.segmentId === selection.segmentId );
            const bundle = segment?.bundles[selection.bundleIndex];
            const offer = bundle?.offers[selection.offerIndex];

            if ( offer?.price !== undefined ) {
                total += offer.price;
            }
        }

        return total;
    }, [ purchasableSegments, selectedOffers ] );

    if ( currentSegment === undefined ) {
        return (
            <div className="flex h-full flex-col items-center justify-center p-6 text-center">
                <p className="text-sm opacity-70">
Aucun segment disponible à l&apos;achat.
                </p>
            </div>
        );
    }

    const isLastSegment = selectionSegmentIndex === purchasableSegments.length - 1;
    const canContinue = currentSelection !== undefined || !wantsTicket;

    const contentBackground = isDarkUi ? "rgba(0, 0, 0, 0.22)" : "rgba(15, 23, 42, 0.05)";
    const cardBackground = palette.modalCardBackground;

    return (
        <div className="flex h-full flex-col">
            <div
                className="flex-1 space-y-3 overflow-y-auto p-[12px]"
                style={{ "backgroundColor": contentBackground }}
            >
                {state.notice !== null && (
                    <p
                        className="rounded-xl border px-3 py-2 text-center text-xs"
                        role="status"
                        style={{
                            "borderColor": palette.overlayBorder,
                            "color":       palette.overlayTextSecondary,
                        }}
                    >
                        {state.notice}
                    </p>
                )}
                {purchasableSegments.length > 1 && (
                    <TicketSegmentTravelProgress
                        activeSegmentIndex={selectionSegmentIndex}
                        isDarkUi={isDarkUi}
                        itinerary={itinerary}
                        purchasableSegments={purchasableSegments}
                    />
                )}
                <div
                    className={`rounded-2xl border p-3 ${ wantsTicket ? "" : "border-dashed" }`}
                    style={{
                        "backgroundColor": cardBackground,
                        "borderColor":     wantsTicket ? palette.overlayBorder : palette.overlayBorderStrong,
                    }}
                >
                    <div className="flex items-stretch gap-2.5">
                        <div
                            className={`flex w-10 shrink-0 flex-col items-center justify-center self-stretch rounded-lg border py-1 ${ wantsTicket ? "" : "opacity-60" }`}
                            style={{
                                "backgroundColor": palette.selectorIdleBackground,
                                "borderColor":     palette.overlayBorder,
                            }}
                        >
                            <span
                                className="text-[8px] font-semibold uppercase leading-none tracking-wide"
                                style={{ "color": palette.overlayTextSecondary }}
                            >
{formatDateParts( currentSegment.from.time ).weekday}
                            </span>
                            <span
                                className="mt-px text-[11px] font-bold leading-tight"
                                style={{ "color": palette.modalCardTextPrimary }}
                            >
{formatDateParts( currentSegment.from.time ).dayMonth}
                            </span>
                        </div>
                        <div className={`flex min-w-0 flex-1 items-stretch gap-2 ${ wantsTicket ? "" : "opacity-60" }`}>
                            <div className="min-w-0 flex-1">
                                <div
                                    className="text-[9px] font-semibold uppercase tracking-widest"
                                    style={{ "color": palette.overlayTextSecondary }}
                                >
Départ
                                </div>
                                <div className="mt-px truncate text-xs font-bold leading-tight tracking-tight">
{formatStopName( currentSegment.from.name )}
                                </div>
                                <div className="mt-0.5 text-sm font-black leading-none tracking-tight">
{formatTime( currentSegment.from.time )}
                                </div>
                            </div>
                            <span
                                className="self-center text-sm"
                                style={{ "color": palette.brandPrimary }}
                            >
→
                            </span>
                            <div className="min-w-0 flex-1 text-right">
                                <div
                                    className="text-[9px] font-semibold uppercase tracking-widest"
                                    style={{ "color": palette.overlayTextSecondary }}
                                >
Arrivée
                                </div>
                                <div className="mt-px truncate text-xs font-bold leading-tight tracking-tight">
{formatStopName( currentSegment.to.name )}
                                </div>
                                <div className="mt-0.5 text-sm font-black leading-none tracking-tight">
{formatTime( currentSegment.to.time )}
                                </div>
                            </div>
                        </div>
                        <button
                            aria-label="Acheter un billet pour ce segment"
                            aria-pressed={wantsTicket}
                            className="flex shrink-0 flex-col items-center gap-0.5 rounded-xl border px-2.5 py-1.5 text-[10px] font-semibold transition-colors"
                            onClick={() => toggleWantsTicket( !wantsTicket )}
                            style={{
                                "backgroundColor": wantsTicket ? palette.brandPrimary : "transparent",
                                "borderColor":     wantsTicket ? palette.brandPrimary : palette.overlayBorderStrong,
                                "borderStyle":     wantsTicket ? "solid" : "dashed",
                                "color":           wantsTicket ? palette.textOnBrandPrimary : palette.overlayTextSecondary,
                            }}
                            type="button"
                        >
                            <IonIcon
                                aria-hidden="true"
                                icon={wantsTicket ? ticket : ticketOutline}
                                style={{ "fontSize": "16px" }}
                            />
                            Billet
                        </button>
                    </div>
                </div>
                {wantsTicket && (
                <div
                    className="rounded-2xl border p-3"
                    style={{
                        "backgroundColor": cardBackground,
                        "borderColor":     palette.overlayBorder,
                    }}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-bold tracking-tight">
{travellerName}
                            </div>
                            <div
                                className="mt-1 flex items-center gap-6 text-xs"
                                style={{ "color": palette.overlayTextSecondary }}
                            >
                                <span className="inline-flex items-center gap-1">
                                    1 ×
                                    <IonIcon
                                        aria-hidden="true"
                                        icon={personOutline}
                                        style={{ "fontSize": "14px" }}
                                    />
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    0 ×
                                    <IonIcon
                                        aria-hidden="true"
                                        icon={bicycleOutline}
                                        style={{ "fontSize": "14px" }}
                                    />
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    0 ×
                                    <IonIcon
                                        aria-hidden="true"
                                        icon={pawOutline}
                                        style={{ "fontSize": "14px" }}
                                    />
                                </span>
                            </div>
                        </div>
                        {/* Edit action placeholder: traveler editing is not wired yet. */}
                        <button
                            aria-label="Modifier le voyageur"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-opacity hover:opacity-70"
                            style={{
                                "borderColor":     palette.overlayBorderStrong,
                                "backgroundColor": palette.overlayCardBackgroundSoft,
                                "color":           palette.overlayTextSecondary,
                            }}
                            type="button"
                        >
                            <IonIcon
                                aria-hidden="true"
                                icon={pencilOutline}
                                style={{ "fontSize": "16px" }}
                            />
                        </button>
                    </div>
                    <div
                        className="my-2 h-px"
                        style={{ "backgroundColor": palette.overlayBorder }}
                    />
                    <div
                        aria-label="Classe"
                        className="relative grid grid-cols-2 rounded-full border p-0.5"
                        role="group"
                        style={{
                            "backgroundColor": palette.selectorIdleBackground,
                            "borderColor":     palette.overlayBorder,
                        }}
                    >
                        <div
                            className="pointer-events-none absolute inset-0.5 overflow-hidden rounded-full"
                        >
                            <div
                                className="h-full w-1/2 rounded-full transition-all duration-200"
                                style={{
                                    "backgroundColor": palette.selectorActiveBackgroundStrong,
                                    "transform":       currentClass === "2" ? "translateX(0%)" : "translateX(100%)",
                                }}
                            />
                        </div>
                        { ( [ "2", "1" ] as const ).map( ( cls ) => {
                            const isSelected = currentClass === cls;

                            return (
                                <button
                                    className="relative z-10 whitespace-nowrap rounded-full px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wide transition-opacity duration-150 hover:opacity-90"
                                    key={cls}
                                    onClick={() => setClass( cls )}
                                    style={{ "color": isSelected ? palette.selectorActiveText : palette.selectorIdleText }}
                                    type="button"
                                >
                                    {cls}
e classe
                                </button>
                            );
                        } ) }
                    </div>
                </div>
                )}
                {wantsTicket && mainOffers.length > 0
? (
                <div
                    aria-label="Offres disponibles"
                    className="space-y-3"
                    role="radiogroup"
                >
                    {mainOffers.map( ( offer ) => {
                        const isDirectlySelected = currentSelection?.bundleIndex === offer.bundleIndex && currentSelection?.offerIndex === offer.offerIndex;

                        // A combined City-Ticket selection keeps the journey ticket row checked.
                        const isRadioChecked = isDirectlySelected || ( isCffJourneyTicket( offer ) && selectedCityKind !== null );
                        const hasCityAddons = isCffJourneyTicket( offer ) && ( leadingAddon !== null || tailingAddon !== null );
                        const tooltipContent = getOfferTooltipDescription( offer.description );

                        return (
                            <div
                                className="rounded-2xl border p-3"
                                key={`${ offer.bundleIndex }-${ offer.offerIndex }`}
                                style={{
                                    "backgroundColor": cardBackground,
                                    "borderColor":     isRadioChecked ? palette.brandPrimary : palette.overlayBorder,
                                }}
                            >
                                <button
                                    aria-checked={isRadioChecked}
                                    className="flex w-full items-center gap-2.5 px-1 py-0.5 text-left transition-colors duration-150 active:scale-[0.99]"
                                    onClick={() => selectOffer( currentSegment.segmentId, offer.bundleIndex, offer.offerIndex, offer.offerIdentifier ?? "" )}
                                    role="radio"
                                    type="button"
                                >
                                    <IonIcon
                                        aria-hidden="true"
                                        className="h-4 w-4 shrink-0"
                                        icon={getCffOfferIcon( offer )}
                                        style={{ "color": isRadioChecked ? palette.brandPrimary : palette.overlayTextSecondary }}
                                    />
                                    <span
                                        className="flex-1 text-sm font-semibold"
                                        style={{ "color": palette.modalCardTextPrimary }}
                                    >
                                        {formatCffOfferLabel( offer.title, offer.productId )}
                                        {tooltipContent !== null && (
                                            <Tooltip
                                                arrow
                                                enterTouchDelay={0}
                                                leaveTouchDelay={7500}
                                                title={tooltipContent}
                                            >
                                                <span
                                                    className="ml-1 align-middle inline-flex h-4 w-4 items-center justify-center"
                                                    onClick={( e ) => {
 e.stopPropagation();
}}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <Info
                                                        className="h-3.5 w-3.5"
                                                        style={{ "color": palette.brandPrimary }}
                                                    />
                                                </span>
                                            </Tooltip>
                                        )}
                                        {SHOW_OFFER_CONDITION_BADGES && (
                                            <span className="mt-0.5 flex gap-1">
                                                {listCffOfferConditions( offer ).map( ( condition ) => (
                                                    <span
                                                        className="rounded-full border px-1.5 text-[9px] uppercase tracking-wide"
                                                        key={condition}
                                                        style={{
                                                            "borderColor": palette.overlayBorder,
                                                            "color":       palette.overlayTextSecondary,
                                                        }}
                                                    >
                                                        {condition}
                                                    </span>
                                                ) )}
                                            </span>
                                        )}
                                    </span>
                                    <span
                                        className="text-sm font-bold"
                                        style={{ "color": palette.modalCardTextPrimary }}
                                    >
                                        {offer.price.toFixed( 2 )}
{" "}
                                        <span className="text-[10px] font-semibold opacity-70">
CHF
                                        </span>
                                    </span>
                                    <IonIcon
                                        aria-hidden="true"
                                        className="h-5 w-5 shrink-0"
                                        icon={isRadioChecked ? checkmarkCircle : ellipseOutline}
                                        style={{ "color": isRadioChecked ? palette.brandPrimary : palette.overlayBorderStrong }}
                                    />
                                </button>
                                {hasCityAddons && (
                                    <div
                                        className="grid transition-[grid-template-rows] duration-300 ease-out"
                                        style={{ "gridTemplateRows": isRadioChecked ? "1fr" : "0fr" }}
                                    >
                                        <div className="min-h-0 overflow-hidden">
                                            <div
                                                className="mt-2 border-t pt-2"
                                                style={{ "borderColor": palette.overlayBorder }}
                                            >
                                                <div className="flex items-center gap-1 px-1 pb-1.5">
                                                    <span
                                                        className="text-[10px] font-semibold uppercase tracking-wide"
                                                        style={{ "color": palette.overlayTextSecondary }}
                                                    >
                                                        Suppléments City
                                                    </span>
                                                    <Tooltip
                                                        arrow
                                                        enterTouchDelay={0}
                                                        leaveTouchDelay={7500}
                                                        title="Le City-Ticket comprend également une carte journalière pour se déplacer (en tram, bus, RER) dans le lieu de départ et/ou de destination sélectionné."
                                                    >
                                                        <span
                                                            className="inline-flex h-4 w-4 items-center justify-center"
                                                            role="button"
                                                            tabIndex={0}
                                                        >
                                                            <Info
                                                                className="h-3.5 w-3.5"
                                                                style={{ "color": palette.brandPrimary }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                                <div className="flex flex-col gap-1.5">
                                                    {leadingAddon !== null && (
                                                        <button
                                                            aria-pressed={isLeadingChecked}
                                                            className="flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-xs font-semibold transition-all duration-150 active:scale-[0.98]"
                                                            onClick={() => toggleCityAddon( "leading" )}
                                                            style={{
                                                                "backgroundColor": palette.interactiveHoverBackground,
                                                                "borderColor":     isLeadingChecked ? palette.brandPrimary : "transparent",
                                                                "color":           palette.modalCardTextPrimary,
                                                            }}
                                                            type="button"
                                                        >
                                                            <IonIcon
                                                                aria-hidden="true"
                                                                className="h-4 w-4 shrink-0"
                                                                icon={locationOutline}
                                                                style={{ "color": isLeadingChecked ? palette.brandPrimary : palette.overlayTextSecondary }}
                                                            />
                                                            <span className="flex-1 text-left">
                                                                City
{" "}
{leadingAddon.label}
                                                            </span>
                                                            <span
                                                                className="font-bold"
                                                                style={{ "color": isLeadingChecked ? palette.brandPrimary : palette.overlayTextSecondary }}
                                                            >
                                                                {formatCityDelta( leadingAddon.offer.price, parcoursOffer?.price )}
                                                            </span>
                                                            <IonIcon
                                                                aria-hidden="true"
                                                                className="h-4.5 w-4.5 shrink-0 text-[18px]"
                                                                icon={isLeadingChecked ? checkmarkCircle : ellipseOutline}
                                                                style={{ "color": isLeadingChecked ? palette.brandPrimary : palette.overlayBorderStrong }}
                                                            />
                                                        </button>
                                                    )}
                                                    {tailingAddon !== null && (
                                                        <button
                                                            aria-pressed={isTailingChecked}
                                                            className="flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-xs font-semibold transition-all duration-150 active:scale-[0.98]"
                                                            onClick={() => toggleCityAddon( "tailing" )}
                                                            style={{
                                                                "backgroundColor": palette.interactiveHoverBackground,
                                                                "borderColor":     isTailingChecked ? palette.brandPrimary : "transparent",
                                                                "color":           palette.modalCardTextPrimary,
                                                            }}
                                                            type="button"
                                                        >
                                                            <IonIcon
                                                                aria-hidden="true"
                                                                className="h-4 w-4 shrink-0"
                                                                icon={locationOutline}
                                                                style={{ "color": isTailingChecked ? palette.brandPrimary : palette.overlayTextSecondary }}
                                                            />
                                                            <span className="flex-1 text-left">
                                                                City
{" "}
{tailingAddon.label}
                                                            </span>
                                                            <span
                                                                className="font-bold"
                                                                style={{ "color": isTailingChecked ? palette.brandPrimary : palette.overlayTextSecondary }}
                                                            >
                                                                {formatCityDelta( tailingAddon.offer.price, parcoursOffer?.price )}
                                                            </span>
                                                            <IonIcon
                                                                aria-hidden="true"
                                                                className="h-4.5 w-4.5 shrink-0 text-[18px]"
                                                                icon={isTailingChecked ? checkmarkCircle : ellipseOutline}
                                                                style={{ "color": isTailingChecked ? palette.brandPrimary : palette.overlayBorderStrong }}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    } )}
                </div>
                )
: (
                <div
                    className="rounded-2xl border p-3"
                    style={{
                        "backgroundColor": cardBackground,
                        "borderColor":     palette.overlayBorder,
                    }}
                >
                    <div
                        className="py-4 text-center text-sm"
                        style={{ "color": palette.overlayTextSecondary }}
                    >
                        {wantsTicket ? "Aucune offre disponible pour cette classe." : "Aucun billet sélectionné pour ce segment."}
                    </div>
                </div>
                )}
                {( parcoursOffer !== null && leadingAddon === null && tailingAddon === null && bothAddon !== null ) && (
                    <p
                        className="rounded-2xl border p-3 text-xs"
                        style={{
                            "backgroundColor": cardBackground,
                            "borderColor":     palette.overlayBorder,
                            "color":           palette.overlayTextSecondary,
                        }}
                    >
                        Ce trajet propose un forfait City combiné incluant &quot;City
{" "}
{bothAddon.label}
&quot;.
                    </p>
                )}
            </div>
            <div
                className="shrink-0 border-t p-[12px]"
                style={{
 "borderColor":     palette.overlayBorder,
"backgroundColor": palette.modalCardBackground,
}}
            >
                <div className="flex items-stretch justify-between gap-2.5">
                    <button
                        aria-label="Précédent"
                        className="flex shrink-0 items-center gap-1 rounded-xl border px-3 text-xs font-semibold transition-opacity hover:opacity-80 disabled:opacity-40"
                        disabled={selectionSegmentIndex === 0}
                        onClick={goToPrevious}
                        style={{
                            "backgroundColor": palette.interactiveHoverBackground,
                            "borderColor":     palette.overlayBorderStrong,
                            "color":           palette.modalCardTextPrimary,
                        }}
                        type="button"
                    >
                        <IonIcon
                            aria-hidden="true"
                            icon={chevronBackOutline}
                            style={{ "fontSize": "16px" }}
                        />
                        Retour
                    </button>
                    <div
                        className={`flex min-h-[48px] flex-1 items-stretch overflow-hidden rounded-xl transition-opacity md:flex-none ${ canContinue ? "" : "opacity-50" }`}
                        style={{ "backgroundColor": palette.brandPrimary }}
                    >
                        <div
                            className="flex shrink-0 flex-col justify-center border-r px-4 py-2"
                            style={{ "borderColor": "rgba(255, 255, 255, 0.25)" }}
                        >
                            <span
                                className="text-[9px] font-semibold uppercase tracking-wider"
                                style={{
 "color":   palette.textOnBrandPrimary,
"opacity": 0.75,
}}
                            >
                                Total
                            </span>
                            <span className="whitespace-nowrap leading-tight">

                                <span
                                    className="text-xs"
                                    style={{ "color": palette.textOnBrandPrimary }}
                                >
                                    CHF
                                    {" "}
                                </span>
                                <span
                                    className="text-md font-bold leading-none tracking-tight"
                                    style={{ "color": palette.textOnBrandPrimary }}
                                >
                                    {totalPrice.toFixed( 2 ).split( "." )[0]}
                                </span>
                                <span
                                    className="text-xs leading-none"
                                    style={{ "color": palette.textOnBrandPrimary }}
                                >
                                    .
                                    {totalPrice.toFixed( 2 ).split( "." )[1]}
                                </span>
                            </span>
                        </div>
                        <button
                            className="flex flex-1 items-center justify-center gap-1.5 px-4 py-2 text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
                            disabled={!canContinue}
                            onClick={goToNext}
                            style={{ "color": palette.textOnBrandPrimary }}
                            type="button"
                        >
                            {isLastSegment ? "Voir le résumé" : "Continuer"}
                            <IonIcon
                                aria-hidden="true"
                                icon={chevronForwardOutline}
                                style={{ "fontSize": "16px" }}
                            />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
